/**
 * Phase 126.B — Classroom Support Hub: ADHD reference page.
 *
 * Public, no-login, shareable URL. Strengths-first; classroom strategy,
 * not diagnosis. Copy is written in teacher voice ("trusted colleague
 * who's been in the room"), strictly per the spec — no clinical
 * language, no prevalence statistics, no "disorder" framing.
 */
import ProfilePageLayout from "../../components/classroom-support/ProfilePageLayout";

export default function ADHD() {
  return (
    <ProfilePageLayout
      slug="adhd"
      metaTitle="ADHD in the Classroom — Atomic Edu"
      metaDescription="What it looks like, the strengths it brings, and what helps. A teacher-to-teacher guide for supporting students with ADHD — no diagnosis, no labels, just what works in your room."
      hero={{
        title: "ADHD in the Classroom",
        tagline:
          "A trusted colleague's take on what it looks like, the strengths these students bring, and the moves that work in real classrooms. No diagnosis, no labels — classroom strategy only.",
      }}
      sections={[
        {
          id: "looks_like",
          body:
            "Every student is different, but here's the texture you might notice across a week — bright, frequent, sometimes contradictory:",
          bullets: [
            "Trouble staying with low-interest tasks — but locked-in hyperfocus on the ones that grab them.",
            "An impulse to answer out loud, jump topics, or follow a side thought before the first one's done.",
            "A body that needs to move. Tilting chairs, foot tapping, doodling, getting up for a sharpener.",
            "Working-memory slips — instructions evaporate between the carpet and the desk.",
            "Time blindness. Ten minutes feels the same as two — until it doesn't.",
            "Organization gaps — the binder, the desk, the locker, the planner.",
          ],
        },
        {
          id: "affects",
          body:
            "Where the friction usually shows up — honest, without making it the whole story:",
          bullets: [
            "Starting and finishing the same task on the same day.",
            "Holding a multi-step instruction long enough to act on it.",
            "Sitting through direct instruction without a release valve.",
            "Transitions — packing up, switching subjects, end-of-period scramble.",
          ],
        },
        {
          id: "strengths",
          body:
            "The reframe matters. The same brain that struggles with the boring also brings:",
          bullets: [
            "Divergent, creative thinking — unexpected connections, wild what-ifs.",
            "Big-picture pattern spotting that other students miss.",
            "Energy and enthusiasm that lifts the whole class on the right day.",
            "Hyperfocus on what genuinely engages them — they can go deeper than anyone.",
            "Spontaneity and humour that builds classroom culture.",
            "Resilience — they've been working around the friction for years.",
          ],
        },
        {
          id: "thrives",
          bullets: [
            "Movement built into the day, not earned as a reward.",
            "Novelty — varied tasks, new angles, surprise hooks.",
            "Short, clear steps with a visible finish line.",
            "Immediate feedback while the work is still warm.",
            "Real choice in how they show what they know.",
            "Genuine interest — connect the goal to something they already care about.",
          ],
        },
        {
          id: "strategies",
          body:
            "The strategies that show up across IEPs, learning support plans, and the best teachers we've worked with — chunk the task, name the start cue, build in movement, ratio 4 positives to every redirect, give a choice of output, set a visible timer. The generator below assembles these into a plan for the situation you type in.",
        },
      ]}
      ctas={undefined}
      generators={[
        {
          to: "/classroom-support/adhd/generator",
          title: "ADHD support plan",
          subtitle:
            "Type the situation (e.g., 'Grade 5, student who shuts down when a task feels too big') and get a plan you can use tomorrow.",
          cta: "Generate a plan",
        },
      ]}
      readNext={[
        {
          to: "/classroom-support/giftedness",
          title: "Giftedness in the Classroom",
          subtitle:
            "Depth over volume — what to do when the student finishes everything in five minutes.",
          cta: "Read the guide",
        },
        {
          to: "/classroom-support/2e",
          title: "Twice-Exceptional (2e)",
          subtitle:
            "When ADHD shows up alongside giftedness, the two often cancel each other out.",
          cta: "Read the guide",
        },
      ]}
    />
  );
}
